import React from 'react';
import listAsString from 'utils/listAsString';
import {
  ManagerProfileAndNameLink,
  RepProfileAndName,
} from 'components/coaching/landing-pages/helpers/ProfileAndName';
import PlanSettingsIcon from 'components/coaching/landing-pages/helpers/buttons/PlanSettingsIcon';
import moment from 'moment';
import formatDateTimeHuman from 'utils/formatDateTimeHuman';
import {PercentChangeIndicator} from 'components/coaching/landing-pages/helpers/PercentChangeIndicator';
import './ActiveRepsList.styl';
import withSorting from 'components/pure/sortable-tables/withSorting';
import {List} from 'immutable';
import SortableTableHeader from 'components/pure/sortable-tables/SortableTableHeader';
import formatSecondsShort from 'utils/formatSecondsShort';
import {
  compareMoments,
  compareNumeric,
  compareReps,
  compareSkills,
  compareStrings,
} from 'components/coaching/landing-pages/helpers/tableSorts';
import PlanProgress from 'components/coaching/landing-pages/helpers/PlanProgress';
import {Toggle} from 'material-ui';
import {Link} from 'react-router';
import {CoachingReviewTypeDisplay} from 'components/dashboard/common/CoachingReviewTypeDisplayAndChooser';
import UpcomingReviewsActions from 'actions/coaching/UpcomingReviewsActions';
import UPCOMING_REVIEW_ROLES from 'actions/coaching/UPCOMING_REVIEW_ROLES';
import {refreshCoachingPlansAndVelocities} from 'components/coaching/landing-pages/RefreshCoachingPlansAndVelocities';
import Button from '@material-ui/core/Button';

const headerData = [
  {label: 'Rep', column: 'rep', className: 'active-rep'},
  {label: 'Skill', column: 'skill', className: 'skill'},
  {
    label: 'Since Plan Start',
    column: 'changeInScore',
    className: 'since-plan-start',
  },
  {label: 'Plan Progress', column: 'planProgress', className: 'plan-progress'},
  {label: 'Coach', column: 'coaches', className: 'coach'},
  {label: 'Next Meeting', column: 'nextReview', className: 'next-review-col'},
  {label: 'Meeting Type', column: 'reviewType', className: 'review-type'},
];

/**
 * Display reps that report to this manager that do have active coaching plans
 *
 * Details plan information, with link to deactivate plan
 */
class ActiveRepsList extends React.PureComponent {
  renderTableRows(
    sortedData,
    adminId,
    managerId,
    coachId,
    manager,
    authUser,
    startDate,
    endDate,
    fromPath
  ) {
    return sortedData.map(
      ({
        rep, // User
        coaches, // List<User>
        plan, // CoachingPlan
        skill, // string
        planProgress, // CoachingPlanProgress
        changeInScore, // number
        nextReview, // Moment
        reviewType, // CoachingReviewType
      }) => {
        const skillName = plan.livecoaching ? 'Live Coaching' : skill;
        return (
          <tr key={plan.id}>
            <td>
              <RepProfileAndName
                rep={rep}
                adminId={adminId}
                managerId={managerId}
                coachId={coachId}
                fromPath={fromPath}
              />
            </td>
            <td className="skill">{skillName}</td>
            <td>
              <PercentChangeIndicator avg={changeInScore} />
            </td>
            <td>
              {planProgress && (
                <PlanProgress
                  repId={rep.id}
                  planId={plan.id}
                  adminId={adminId}
                  managerId={managerId}
                  coachId={coachId}
                  fromPath={fromPath}
                  renderPlanProgress={() => renderPlanProgress(planProgress)}
                  makePlanPopoverContent={() => makePlanPopoverContent(plan, planProgress)}
                />
              )}
            </td>
            <td className="coach">
              <div className="multiple-avatars">
                {coaches.map(coach => (
                  <ManagerProfileAndNameLink
                    adminId={adminId}
                    managerId={managerId}
                    manager={manager}
                    mgr={coach}
                    key={coach.userId}
                    disabled={!authUser.isAdmin()}
                  />
                ))}
              </div>
            </td>
            <td>
              <div className="date-data">
                {nextReview && formatDateTimeHuman(moment(nextReview))}
              </div>
            </td>
            <td className="review-type">
              {reviewType && <CoachingReviewTypeDisplay reviewType={reviewType} briefLabel />}
            </td>
            <td>
              <PlanSettingsIcon
                plan={plan}
                review={plan.getNextReview()}
                rep={rep}
                reviewFocused={false}
                refreshCoachingData={() => refreshCoachingPlansAndVelocities(plan.coach.id)}
                refreshUpcomingReviews={this.refreshUpcomingReviews}
              />
            </td>
          </tr>
        );
      }
    );
  }

  refreshUpcomingReviews = () => {
    UpcomingReviewsActions.upcomingReviews(
      this.props.authUser.id,
      this.props.coachId ? UPCOMING_REVIEW_ROLES.ALL : UPCOMING_REVIEW_ROLES.COACH
    );
  };

  render() {
    const {
      sortedData,
      sortColumn,
      sortDirection,
      onSort,
      startDate,
      endDate,
      adminId,
      managerId,
      coachId,
      manager,
      authUser,
      fromPath,
      filterByTeam,
      toggleTeamFilter,
      showFilterToggle,
      showMoreLink,
    } = this.props;

    return (
      <div className="mgr-landing-card-container">
        <div className="header-wrapper">
          <h2 className="subheader">{this.props.tableHeader}</h2>
          {showFilterToggle && (
            <Toggle
              className="team-filter-toggle"
              label="Selected Team Only"
              labelPosition="left"
              toggled={filterByTeam}
              onToggle={toggleTeamFilter}
            />
          )}
        </div>
        <table className="rep-list">
          <thead>
            <tr>
              {headerData.map(data => (
                <th key={data.label} className={data.className}>
                  <SortableTableHeader
                    label={data.label}
                    renderColumn={data.column}
                    onClick={() => onSort(data.column, sortDirection)}
                    sortDirection={sortDirection}
                    sortColumn={sortColumn}
                  />
                </th>
              ))}
              <th className="action-gear">&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {this.renderTableRows(
              sortedData,
              adminId,
              managerId,
              coachId,
              manager,
              authUser,
              startDate,
              endDate,
              fromPath
            )}
          </tbody>
        </table>
        {showMoreLink && (
          <div className="show-more-link">
            <Button component={Link} variant="text" to={showMoreLink}>
              Show More
            </Button>
          </div>
        )}
      </div>
    );
  }
}

function renderPlanProgress(planProgress) {
  if (!planProgress) {
    return '';
  }
  const {completed, total} = planProgress;

  if (completed === 0) return 'No meetings yet';
  if (!total) return `${completed} Meetings`;

  return `${completed} / ${total} Meetings`;
}

function makePlanPopoverContent(plan, planProgress) {
  const completedReviews = plan.reviews.filter(r => r.status === 'COMPLETED').size;
  const scheduleTimeInMinutes = plan.schedule && plan.schedule.durationInMinutes;
  const reviewTimeInMinutes = formatSecondsShort(
    completedReviews * scheduleTimeInMinutes * 60,
    true,
    true,
    false
  );

  return (
    <div>
      {planProgress && (
        <p>
          {planProgress.completed || 0}
          {planProgress.total && ` out of ${planProgress.total}`} planned meetings have taken place.
        </p>
      )}
      {planProgress && planProgress.projectedEnd && (
        <p>Plan is due to end {planProgress.projectedEnd.format('MMM Do YYYY')}.</p>
      )}
      <p>{reviewTimeInMinutes} of meetings have taken place.</p>
    </div>
  );
}

/**
 * Given store info, massage and create raw data to feed into the sortable table at a plan level
 * Note that the table is ostensibly "by rep" but the rows here are ultimately by plan - we simply add more rows
 * to the table for reps with multiple plans.
 *
 * @param coachingPlans - coaching plans for those reps
 * @param planVelocities - plan velocity info from the DirectReportsStore
 * @param users - map of users from the UserStore.
 * @param reviewTypes - map of review type id to review type.
 * @returns {List<any>|*}
 */
function gatherData({coachingPlans, planVelocities, users, reviewTypes}) {
  if (!coachingPlans || coachingPlans.size === 0) {
    return List();
  }

  return (
    coachingPlans
      // Some api calls fetch completed plans, but we don't ever want to display them in this table.
      // Filter them out.
      .filter(plan => !plan.completed)
      .map(plan => {
        const velocity = planVelocities && planVelocities.find(pv => pv.planId === plan.id);
        const scheduledReview = plan.reviews.find(r => r.status === 'SCHEDULED');
        const nextReviewCoach = scheduledReview && users.get(scheduledReview.coachUserId);
        const planCoach = plan.coach.info;
        const coaches =
          nextReviewCoach && nextReviewCoach.userId !== planCoach.userId
            ? List([planCoach, nextReviewCoach])
            : List([planCoach]);

        const review = plan.getNextReview(false);
        const reviewType =
          // avoid searching if review type not set. will not be found, so a waste of time.
          review &&
          review.reviewTypeId &&
          reviewTypes.find(reviewType => reviewType.id === review.reviewTypeId);

        return {
          rep: plan.recipient.info,
          coaches,
          plan,
          skill: listAsString(plan.contentDetails.map(detail => detail.skillName || detail.custom)),
          planProgress: velocity && velocity.planProgress,
          changeInScore: velocity && velocity.changeInScore,
          nextReview: scheduledReview && scheduledReview.scheduleAt,
          reviewType,
        };
      })
  );
}

function compareCoaches(l, r) {
  const leftCoach = l.get(l.size - 1);
  const rightCoach = r.get(r.size - 1);
  return compareStrings(leftCoach.getDisplayName(), rightCoach.getDisplayName());
}

function compareReviewTypes(l, r) {
  return compareStrings(l && l.name, r && r.name);
}

export default withSorting(ActiveRepsList, props => gatherData(props), {
  rep: compareReps,
  coaches: (l, r) => compareCoaches(l.coaches, r.coaches),
  skill: compareSkills,
  planProgress: (l, r) => compareNumeric(r.planProgress.completed, l.planProgress.completed),
  changeInScore: (l, r) => compareNumeric(r.changeInScore, l.changeInScore),
  nextReview: (l, r) => compareMoments(l.nextReview, r.nextReview),
  reviewType: (l, r) => compareReviewTypes(l.reviewType, r.reviewType),
});
